import type { Variable } from '@fleequid-secrets/shared';
import { type CSSProperties, memo, type ReactNode } from 'react';
import { ago, initials, MASK, valuePreview } from '@/lib/format';
import { Icon } from './Icon';

export interface RowActions {
    onCursor: (id: string) => void;
    onToggle: (variable: Variable) => void;
    onCopy: (variable: Variable) => void;
    onEdit: (variable: Variable) => void;
    onHistory: (variable: Variable) => void;
    onDelete: (variable: Variable) => void;
}

// One variable: its key, its value masked until asked for (then shown for a minute, the line under it running out),
// its note, who changed it last. What can be done to it comes with the pointer or the keyboard's cursor.
export const VariableRow = memo(function VariableRow(
    props: {
        variable: Variable;
        value: string | undefined;
        until: number | undefined;
        cursor: boolean;
        flash: boolean;
        armed: boolean;
        now: number;
    } & RowActions,
): ReactNode {
    const { variable, value } = props;
    const preview = value === undefined ? null : valuePreview(value);

    return (
        <div
            className={`vrow ${props.cursor ? 'cursor' : ''} ${props.flash ? 'flash' : ''}`}
            data-id={variable.id}
            onMouseDown={() => props.onCursor(variable.id)}
            onDoubleClick={() => props.onEdit(variable)}
        >
            <div className="vkey">
                <b title={variable.key}>{variable.key}</b>
            </div>
            <div
                className={`vval ${preview ? 'revealed' : ''}`}
                style={props.until ? ({ '--hide-after': `${Math.max(0, props.until - Date.now())}ms` } as CSSProperties) : undefined}
                key={props.until ?? 'masked'}
                title={preview ? 'Hide the value (space)' : 'Reveal the value (space)'}
                onClick={() => props.onToggle(variable)}
            >
                {preview === null ? (
                    <span className="masked">{MASK}</span>
                ) : value === '' ? (
                    <span className="chip">empty</span>
                ) : (
                    <>
                        <span className="shown">{preview.line || ' '}</span>
                        {preview.more > 0 && <span className="chip">+{preview.more} lines</span>}
                    </>
                )}
            </div>
            <div className="vnote" title={variable.note}>
                {variable.note}
            </div>
            <div
                className="vmeta"
                title={variable.updatedBy ? `Version ${variable.version}, by ${variable.updatedBy.name}` : `Version ${variable.version}`}
            >
                {variable.updatedBy && <span className="avatar small">{initials(variable.updatedBy.name)}</span>}
                {ago(variable.updatedAt, props.now)}
            </div>
            <div className="vacts">
                <button
                    className={`iconbtn ${preview ? 'on' : ''}`}
                    title={preview ? 'Hide (space)' : 'Reveal (space)'}
                    onClick={() => props.onToggle(variable)}
                >
                    <Icon name={preview ? 'eyeoff' : 'eye'} size={16} />
                </button>
                <button className="iconbtn" title="Copy the value (C)" onClick={() => props.onCopy(variable)}>
                    <Icon name="copy" size={16} />
                </button>
                <button className="iconbtn" title="History and who looked (H)" onClick={() => props.onHistory(variable)}>
                    <Icon name="history" size={16} />
                </button>
                <button className="iconbtn" title="Edit (↵)" onClick={() => props.onEdit(variable)}>
                    <Icon name="pencil" size={16} />
                </button>
                {props.armed ? (
                    <button className="act small danger" onClick={() => props.onDelete(variable)} autoFocus>
                        Delete?
                    </button>
                ) : (
                    <button className="iconbtn danger" title="Delete" onClick={() => props.onDelete(variable)}>
                        <Icon name="trash" size={16} />
                    </button>
                )}
            </div>
        </div>
    );
});
